import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkle, Trophy, XCircle, ChatCircle } from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * AI Negotiation Reviews Card — displays GPT-5.2's self-review of the
 * pilot's closed bids, plus a running average calibration score.
 *
 * Every time a bid closes (won or lost) the backend runs a background
 * review that grades the anchor decision against the broker's history
 * that predated the bid. This surface makes those reviews readable.
 *
 * Auto-hides when the user has no closed bids yet.
 */
export default function NegotiationReviewsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    client.get("/negotiation-reviews?limit=8")
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && toast.error(e?.response?.data?.detail || "Reviews load failed"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="card-surface p-6" data-testid="reviews-card-loading">
        <div className="animate-pulse text-neutral-500 text-sm">Loading AI reviews…</div>
      </div>
    );
  }
  if (!data || !data.reviews?.length) return null;

  return (
    <div className="card-surface p-6" data-testid="reviews-card">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-indigo-300 mb-1">
            <ChatCircle size={13} weight="bold" />
            AI NEGOTIATION REVIEWS
          </div>
          <h3 className="font-display text-lg font-semibold text-white">
            GPT-5.2 graded your last {Math.min(data.reviews.length, 8)} closed bid{data.reviews.length === 1 ? "" : "s"}
          </h3>
          <p className="text-[11px] text-neutral-500 mt-1 max-w-lg">
            The AI grades every closed bid against the broker's known bracket
            <span className="text-neutral-400"> at the time of the bid</span>{" "}
            — no data leakage from the outcome.
          </p>
        </div>
        {data.avg_calibration_score != null && (
          <div className="flex items-center gap-3 shrink-0">
            <ScoreDial score={data.avg_calibration_score} count={data.total} />
          </div>
        )}
      </div>

      <div className="space-y-2" data-testid="reviews-list">
        {data.reviews.map((r) => (
          <ReviewRow key={r.id} review={r} />
        ))}
      </div>
    </div>
  );
}


function ScoreDial({ score, count }) {
  // 1-10 score → color band
  const tone = score >= 8
    ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"
    : score >= 6
      ? "text-[#FF5A1F] bg-[#FF5A1F]/10 border-[#FF5A1F]/30"
      : "text-amber-400 bg-amber-500/10 border-amber-500/30";
  return (
    <div className={`inline-flex flex-col items-center gap-0.5 rounded-lg border px-4 py-2 ${tone}`}>
      <div className="text-2xl font-display font-bold tabular-nums leading-none">{score}</div>
      <div className="text-[9px] uppercase tracking-widest opacity-80">avg / 10 · {count} bids</div>
    </div>
  );
}


function ReviewRow({ review }) {
  const isWon = review.outcome === "won";
  const s = review.calibration_score || 5;
  const scoreTone = s >= 8
    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
    : s >= 6
      ? "border-[#FF5A1F]/40 bg-[#FF5A1F]/10 text-[#FF5A1F]"
      : "border-amber-500/40 bg-amber-500/10 text-amber-300";
  const OutcomeIcon = isWon ? Trophy : XCircle;
  const outcomeTone = isWon ? "text-emerald-300" : "text-rose-300";

  const noteText = review.ai_note || review.rule_reason || "";
  const brokerSlug = (review.broker_company || "").replace(/\s+/g, '-').toLowerCase();

  return (
    <div className="rounded-lg border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] transition-colors p-3" data-testid={`review-row-${brokerSlug}`}>
      <div className="flex items-start gap-3">
        {/* Score pill */}
        <div className={`shrink-0 inline-flex items-center justify-center min-w-[42px] h-8 rounded-full border font-mono text-sm font-bold tabular-nums ${scoreTone}`}>
          {s}
        </div>
        {/* Body */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <OutcomeIcon size={12} weight="bold" className={outcomeTone} />
            <span className={`text-[10px] uppercase tracking-widest font-semibold ${outcomeTone}`}>
              {isWon ? "WON" : "LOST"}
            </span>
            <span className="text-sm text-white font-medium truncate">{review.broker_company}</span>
            <span className="text-[11px] text-neutral-500">
              · asked ${Number(review.ask_amount || 0).toLocaleString()}
              {review.ask_per_mile != null && ` (${review.ask_per_mile.toFixed(2)}/mi)`}
            </span>
          </div>
          <div className="text-xs text-neutral-300 leading-relaxed mt-1">
            {review.ai_note ? (
              <span>
                <Sparkle size={10} weight="fill" className="text-[#FF5A1F] inline mr-1" />
                {noteText}
              </span>
            ) : (
              <span className="text-neutral-400">{noteText}</span>
            )}
          </div>
          {/* Bracket context */}
          {(review.floor_per_mile_at_close != null || review.ceiling_per_mile_at_close != null) && (
            <div className="text-[11px] text-neutral-500 mt-1.5 tabular-nums">
              At close:{" "}
              {review.floor_per_mile_at_close != null && (
                <>floor <b className="text-emerald-300">${review.floor_per_mile_at_close.toFixed(2)}/mi</b></>
              )}
              {review.floor_per_mile_at_close != null && review.ceiling_per_mile_at_close != null && " · "}
              {review.ceiling_per_mile_at_close != null && (
                <>ceiling <b className="text-rose-300">${review.ceiling_per_mile_at_close.toFixed(2)}/mi</b></>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
